<template>
    <div class="tool-block my-2" :class="blockClass">
        <!-- Header row -->
        <div class="flex items-center gap-2 px-3 py-1.5">
            <UIcon :name="statusIcon" class="w-4 h-4 flex-shrink-0" :class="iconClass" />
            <span class="font-medium text-slate-700">{{ displayTitle }}</span>
            <span class="flex-1 min-w-0" />
            <span v-if="isToolProcessing" class="text-[11px] text-blue-500 animate-pulse whitespace-nowrap">preparing…</span>
            <span v-else-if="uploadState === 'importing'" class="text-[11px] text-blue-500 animate-pulse whitespace-nowrap">importing…</span>
            <span v-else-if="uploadState === 'done'" class="text-[11px] text-green-600 whitespace-nowrap">uploaded</span>
        </div>

        <!-- Upload card (tool output available, not yet uploaded) -->
        <div v-if="showUploadCard" class="mx-3 mb-2">
            <div class="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-4">
                <div class="flex items-center gap-3">
                    <div class="flex-shrink-0 w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center">
                        <UIcon :name="exclusionType === 'domains' ? 'i-ion-ban' : 'i-ion-person-remove'" class="w-5 h-5 text-red-600" />
                    </div>
                    <div class="flex-1 min-w-0">
                        <p class="text-sm font-medium text-slate-700">{{ displayTitle }}</p>
                        <p class="text-xs text-slate-500 mt-0.5">
                            {{ exclusionType === 'domains'
                                ? 'CSV with company domains to exclude (customers, partners, etc.)'
                                : 'CSV with email addresses of contacts to exclude' }}
                        </p>
                    </div>
                    <UButton color="primary" size="sm" :disabled="!chatId" @click="importerOpen = true">
                        <UIcon name="i-ion-cloud-upload" class="w-4 h-4 mr-1" />
                        Upload CSV
                    </UButton>
                </div>
            </div>
        </div>

        <!-- Upload result -->
        <div v-if="uploadState === 'done' && uploadResult" class="mx-3 mb-2">
            <div class="rounded-lg border border-green-200 bg-green-50 p-3">
                <div class="flex items-center gap-2">
                    <UIcon name="i-ion-checkmark-circle" class="w-5 h-5 text-green-600 flex-shrink-0" />
                    <div>
                        <p class="text-sm font-medium text-green-800">
                            {{ uploadResult.count }} {{ exclusionType === 'domains' ? 'domains' : 'contacts' }} excluded
                        </p>
                        <p v-if="uploadResult.fileName" class="text-xs text-green-600 mt-0.5">
                            from {{ uploadResult.fileName }}
                        </p>
                    </div>
                </div>
            </div>
        </div>

        <!-- Error state -->
        <div v-if="uploadState === 'error'" class="mx-3 mb-2 px-2.5 py-2 rounded-md bg-red-50 border border-red-100 text-red-600 text-xs">
            {{ uploadError }}
        </div>

        <!-- CSV Importer Modal -->
        <SharedCSVImporter
            v-if="importerOpen"
            v-model:open="importerOpen"
            :sheet="importerSheet"
            :cloud="{ targetKind: exclusionType === 'domains' ? 'excluded-accounts' : 'excluded-contacts', chatId, chatActionId: part.toolCallId }"
            :on-complete="handleImportComplete"
        />
    </div>
</template>

<script setup lang="ts">
import type { ImportResult as CloudImportResult, ImportSessionDTO as CloudImportSession } from '@coldreach/utils/cloud-imports';
const props = defineProps<{
    chatId?: string;
    onUploadComplete: (result: ChatExclusionResult) => Promise<void>;
    part: {
        type: string;
        toolCallId?: string;
        state?: string;
        input?: unknown;
        output?: unknown;
        errorText?: string;
        [key: string]: unknown;
    };
}>();

type ChatExclusionResult = {
        importSessionId: string;
        chatId?: string;
        count: number;
        type: 'domains' | 'contacts';
        fileName: string;
};

// Parse tool input/output
const partInput = computed(() => (props.part.input || {}) as Record<string, unknown>);
const partOutput = computed(() => (props.part.output || {}) as Record<string, unknown>);

const exclusionType = computed(() => {
    return (partOutput.value.type || partInput.value.type || 'domains') as 'domains' | 'contacts';
});

// Tool state detection
const isToolProcessing = computed(() => {
    if (props.part.output !== undefined) return false;
    if (props.part.errorText) return false;
    return true;
});

const isToolCompleted = computed(() =>
    props.part.state === 'output-available' ||
    (props.part.output !== undefined && !props.part.errorText)
);

// Local upload state
const uploadState = ref<'idle' | 'importing' | 'done' | 'error'>('idle');
const uploadResult = ref<{ count: number; fileName: string } | null>(null);
const uploadError = ref<string>('');
const importerOpen = ref(false);

const showUploadCard = computed(() =>
    isToolCompleted.value && uploadState.value === 'idle'
);

// Display
const displayTitle = computed(() => {
    if (exclusionType.value === 'domains') return 'Exclude Accounts';
    return 'Exclude Contacts';
});

const statusIcon = computed(() => {
    if (props.part.errorText) return 'i-heroicons-x-circle';
    if (uploadState.value === 'done') return 'i-heroicons-check-circle';
    if (uploadState.value === 'error') return 'i-heroicons-x-circle';
    if (isToolProcessing.value || uploadState.value === 'importing') return 'i-heroicons-arrow-path';
    return 'i-ion-ban';
});

const iconClass = computed(() => {
    if (props.part.errorText || uploadState.value === 'error') return 'text-red-500';
    if (uploadState.value === 'done') return 'text-green-500';
    if (isToolProcessing.value || uploadState.value === 'importing') return 'text-blue-500 animate-spin';
    return 'text-red-500';
});

const blockClass = computed(() => {
    if (props.part.errorText || uploadState.value === 'error') return 'tool-block--error';
    if (uploadState.value === 'done') return 'tool-block--success';
    if (isToolProcessing.value || uploadState.value === 'importing') return 'tool-block--active';
    return 'tool-block--active';
});

// CSVImporter config
const importerSheet = computed(() =>
    exclusionType.value === 'domains' ? excludedDomainsSheet : excludedContactsSheet
);

async function handleImportComplete(result: CloudImportResult, session: CloudImportSession) {
    await props.onUploadComplete({ importSessionId: session.id, chatId: session.chatId ?? props.chatId, count: session.activeRowCount, type: exclusionType.value, fileName: result.fileName ?? 'Pasted data' });
    uploadState.value = 'done';
    uploadResult.value = { count: session.activeRowCount, fileName: result.fileName ?? 'Pasted data' };
}
</script>

<style scoped>
.tool-block {
    border-left: 3px solid rgb(203 213 225);
    border-radius: 6px;
    background: rgb(248 250 252 / 0.6);
    font-size: 12px;
}

.tool-block--error {
    border-left-color: rgb(239 68 68);
    background: rgb(254 242 242 / 0.5);
}

.tool-block--active {
    border-left-color: rgb(59 130 246);
    background: rgb(239 246 255 / 0.5);
}

.tool-block--success {
    border-left-color: rgb(34 197 94);
    background: rgb(240 253 244 / 0.4);
}
</style>
